import React from "react";
import dayjs from "dayjs";
import { isNull, isString, isNumber } from "lodash";

import {
  SummaryFormatValueTagType,
  SummaryFormatValueItem,
  AggregateFunctions,
  IAggregateFormValue,
  StatisticsType,
} from "../types/form-type";
import {
  ICurrencyTypeOptions,
  IRatingOptions,
  INumberTypeOptions,
  IDateTimeTypeOptions,
  TimeFormat,
  ITableData,
  ITableColumn,
  ColumnType,
  NumberFormat,
  DateFormat,
} from "../types/table-type";
import { Language } from "../types/global-type";

import { getRealColumn, getOriginColumn } from "./option_main";
import { formatAggregateValue } from "./form_secondary";
import { formatMessage } from "../lib/intl";

// =================== 富文本级别的 format value ===================
const RatingIcon = (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.37546 1.86858C9.88557 1.02094 11.1145 1.02094 11.6246 1.86858L14.1015 5.98437C14.2848 6.28889 14.5837 6.50607 14.9299 6.58626L19.6097 7.6701C20.5735 7.89332 20.9532 9.06206 20.3047 9.80914L17.1558 13.4367C16.9228 13.7051 16.8086 14.0565 16.8393 14.4106L17.2547 19.1962C17.3402 20.1818 16.346 20.9041 15.4351 20.5182L11.012 18.6444C10.6848 18.5057 10.3153 18.5057 9.98802 18.6444L5.56495 20.5182C4.65403 20.9041 3.65984 20.1818 3.74537 19.1962L4.1607 14.4106C4.19143 14.0565 4.07725 13.7051 3.84427 13.4367L0.695332 9.80914C0.0468139 9.06206 0.426562 7.89332 1.39035 7.6701L6.07011 6.58626C6.41636 6.50607 6.71527 6.28889 6.89854 5.98437L9.37546 1.86858Z"
      fill="#FFC561"
    />
  </svg>
);

const CLASS_BY_TAG_TYPE: { [key in SummaryFormatValueTagType]: string } = {
  [SummaryFormatValueTagType.STANDARD_HEIGHT]: "summary-value-item-standard-height",
  [SummaryFormatValueTagType.ONE_HALF_HEIGHT]: "summary-value-item-one-half-height",
  [SummaryFormatValueTagType.ONE_THIRD_HEIGHT]: "summary-value-item-one-third-height",
};

const getSummaryFormatValueHTML = (list: SummaryFormatValueItem[]): JSX.Element[] => {
  const result = list.map((item) => {
    return <span className={CLASS_BY_TAG_TYPE[item.tagType]}>{item.value}</span>;
  });
  return result;
};

export const formatHTMLByCurrency = (value: number, typeOptions: ICurrencyTypeOptions): JSX.Element | JSX.Element[] => {
  const { precision, currencyFormat } = typeOptions;
  const currencyFormatted = value.toLocaleString("en", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  });
  const result = getSummaryFormatValueHTML([
    { tagType: SummaryFormatValueTagType.ONE_HALF_HEIGHT, value: currencyFormat },
    {
      tagType: SummaryFormatValueTagType.STANDARD_HEIGHT,
      value: currencyFormatted,
    },
  ]);
  return result;
};

export const formatHTMLByRating = (value: number, typeOptions: IRatingOptions): JSX.Element | JSX.Element[] => {
  const valueStr = Number.isInteger(value) ? `${value}` : `${value.toFixed(1)}`;
  const result = [<span className="summary-value-item-standard-height">{valueStr}</span>, RatingIcon];
  return result;
};

export const formatHTMLByNumberPercentage = (
  value: number,
  typeOptions: INumberTypeOptions
): JSX.Element | JSX.Element[] => {
  const { precision } = typeOptions;
  const result = getSummaryFormatValueHTML([
    { tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: (value * 100).toFixed(precision) },
    { tagType: SummaryFormatValueTagType.ONE_HALF_HEIGHT, value: "%" },
  ]);
  return result;
};

// 通用 % 格式化
export const formatHTMLByBasicPercentage = (value: number): JSX.Element | JSX.Element[] => {
  const result = getSummaryFormatValueHTML([
    { tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: `${value}` },
    { tagType: SummaryFormatValueTagType.ONE_HALF_HEIGHT, value: "%" },
  ]);
  return result;
};

export const formatHTMLByDate = (value: string, typeOptions: IDateTimeTypeOptions): JSX.Element | JSX.Element[] => {
  const { timeFormat, includeTime } = typeOptions;
  const dateObj = dayjs(value);
  const list = [
    { tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: dateObj.format("YYYY") },
    { tagType: SummaryFormatValueTagType.ONE_THIRD_HEIGHT, value: "年" },
    { tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: dateObj.format("MM") },
    { tagType: SummaryFormatValueTagType.ONE_THIRD_HEIGHT, value: "月" },
    { tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: dateObj.format("DD") },
    { tagType: SummaryFormatValueTagType.ONE_THIRD_HEIGHT, value: "日" },
  ];
  if (includeTime) {
    if (timeFormat === TimeFormat.TWELVE_HOUR) {
      // 12 小时制
      list.push({ tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: dateObj.format("h:mm") });
      list.push({ tagType: SummaryFormatValueTagType.ONE_THIRD_HEIGHT, value: dateObj.format("A") });
    } else {
      // 24 小时制
      list.push({ tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: dateObj.format("H:mm") });
    }
  }
  return getSummaryFormatValueHTML(list);
};

export const formatHTMLByBasicDate = (value: number, aggFunc: AggregateFunctions): JSX.Element | JSX.Element[] => {
  const unit =
    aggFunc === AggregateFunctions.DATE_RANGE_DAYS
      ? formatMessage({ id: "form.option.time.cycle.day" })
      : formatMessage({ id: "dropdown.aggregate.prefix.dateRangeMonth" });
  const list = [
    { tagType: SummaryFormatValueTagType.STANDARD_HEIGHT, value: value.toFixed(0) },
    { tagType: SummaryFormatValueTagType.ONE_THIRD_HEIGHT, value: unit },
  ];
  return getSummaryFormatValueHTML(list);
};
// =================== 富文本级别的 format value ===================

const getFormatValueToHTML = (
  value: number | string,
  calculationType: AggregateFunctions,
  column: ITableColumn,
  locale: Language
): JSX.Element[] | JSX.Element | null => {
  switch (calculationType) {
    case AggregateFunctions.SUM:
    case AggregateFunctions.AVERAGE:
    case AggregateFunctions.MEDIAN:
    case AggregateFunctions.MIN:
    case AggregateFunctions.MAX:
    case AggregateFunctions.RANGE:
    case AggregateFunctions.STANDARD_DEVIATION: {
      switch (column.type) {
        case ColumnType.CURRENCY:
          return formatHTMLByCurrency(value as number, column.typeOptions as ICurrencyTypeOptions);
        case ColumnType.RATING:
          return formatHTMLByRating(value as number, column.typeOptions as IRatingOptions);
        case ColumnType.NUMBER:
          const {
            typeOptions: { numberFormat },
          } = column as ITableColumn<ColumnType.NUMBER>;
          // 百分比
          if (numberFormat === NumberFormat.PERCENTAGE)
            return formatHTMLByNumberPercentage(value as number, column.typeOptions as INumberTypeOptions);
          return null;
        default:
          return null;
      }
    }
    case AggregateFunctions.LATEST_DATE:
    case AggregateFunctions.EARLIEST_DATE:
      if (locale === Language.zh) {
        const {
          typeOptions: { dateFormat },
        } = column as ITableColumn<ColumnType.DATETIME>;
        if (dateFormat === DateFormat.FRIENDLY) {
          return formatHTMLByDate(value as string, column.typeOptions as IDateTimeTypeOptions);
        }
      }
      return null;
    case AggregateFunctions.DATE_RANGE_DAYS:
    case AggregateFunctions.DATE_RANGE_MONTH:
      return formatHTMLByBasicDate(value as number, calculationType);
    case AggregateFunctions.PERCENTAGE_CHECKED:
    case AggregateFunctions.PERCENTAGE_EMPTY:
    case AggregateFunctions.PERCENTAGE_FILLED:
    case AggregateFunctions.PERCENTAGE_UNCHECKED:
    case AggregateFunctions.PERCENTAGE_UNIQUE:
      return formatHTMLByBasicPercentage(value as number);
  }
  return null;
};

export const formatSummaryValue = (
  value: number | string,
  formValue: IAggregateFormValue,
  tableData: ITableData,
  locale: Language
): JSX.Element[] | JSX.Element => {
  // 处理 value 为 空 or 非法值 的情况 返回 --
  if ((isString(value) && value.length === 0) || (isNumber(value) && isNaN(value))) {
    return [<span>--</span>];
  }

  const { calculationTargetId, statisticsType, calculationType } = formValue;
  if (statisticsType === StatisticsType.COUNT) return <span>{value}</span>;
  if (!calculationTargetId) return <span>{value}</span>;
  const calculationColumn = getOriginColumn(tableData, calculationTargetId);
  const realColumn = getRealColumn(calculationTargetId, tableData);
  if (!calculationColumn || !realColumn) return <span>{value}</span>;

  const richText = getFormatValueToHTML(value, calculationType, realColumn, locale);
  if (!isNull(richText)) {
    return richText;
  }

  const text = formatAggregateValue(value, calculationType, realColumn.typeOptions, realColumn.type);
  return [<span>{text}</span>];
};
